import { Alert as MuiAlert, Box, Button, CircularProgress, Grid, Typography } from '@mui/material'
import { FC, useContext, useState, useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'

// component import
import phone from 'phone'
import { invalidMatterClick } from '../../../utils'
import { formatAddress } from '../../../utils/legalTeam'
import { yupResolver } from '@hookform/resolvers/yup'
import { CreateCaseCustomInput, ErrorException } from '../../../interfaceTypes'
import { AuthContext, CaseContext } from '../../../context'
import { createCaseValidationSchema } from '../../../validationSchema'
import { DefaultValues, FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import {
  CASE_CREATED_SUCCESS,
  CREATE_CASE_STEPS,
  GRAPHQL_QUERY_POLICY,
  INTEREST_CONFIGURATION,
  QUERY_PARAMS,
  stepFields
} from '../../../constants'
import {
  BillGenerationType,
  BillingType,
  CasePayload,
  CaseStatus,
  DiscountType,
  ExternalUser,
  ExternalUserCaseRole,
  ExternalUserStatus,
  UserType,
  useCreateCaseMutation,
  useFetchCaseQuery,
  useGetClientByClientNumberLazyQuery
} from '../../../../generated'
import CaseStepper from '../../caseDetail/caseDetailCard/updateCase/CaseStepper'

// others
import { Alert } from '../../common/Alert'
import References from './References'
import BasicDetails from './basicDetails'
import BillingDetails from './billingDetails'
import CaseParties from './caseParties'
import LegalTeam from './legalTeam'
import { AddNewClient } from './basicDetails/AddNewClient'
import { AddNewAdversary } from './basicDetails/AddNewAdversary'
import { endOfDay } from 'date-fns'
import dayjs from 'dayjs'
import { buildDuplicateCaseValues } from '../../../utils/caseFormValues'

interface DuplicateSource {
  values: DefaultValues<CreateCaseCustomInput>
  selectedLawTypeId?: string
  label: string
}

interface CreateCaseFormProps {
  // Set when duplicating: the source matter's details pre-fill the form, which then creates a new matter as usual.
  duplicateSource?: DuplicateSource
}

// Copied billing keeps the form's defaults (interest rates, discount type, ...) for anything the source left unset.
const withoutUndefined = <T extends object>(value: T) =>
  Object.fromEntries(Object.entries(value).filter(([, item]) => item !== undefined)) as Partial<T>

const CreateCaseForm: FC<CreateCaseFormProps> = ({ duplicateSource }) => {
  const [searchParams] = useSearchParams()
  const clientNo = searchParams.get(QUERY_PARAMS.CLIENT)

  const [activeStep, setActiveStep] = useState(0)
  const [newClientAdded, setNewClientAdded] = useState<ExternalUser>()
  const [newAdversaryAdded, setNewAdversaryAdded] = useState<ExternalUser>()
  const { currentUser } = useContext(AuthContext)
  const { clientRoleId, judgeRoleId, adversaryRoleId } = useContext(CaseContext)
  // `loading` only flips once the mutation is in flight, which is after the awaited validation
  // below — leaving a window where the button is still live. A ref latches synchronously on the
  // very first click so a second one cannot start a duplicate create.
  const submittingRef = useRef(false)
  const [submitting, setSubmitting] = useState(false)
  const clientListRef = useRef<{ appendClientUser: (user: ExternalUser) => void }>(null)
  const adversaryListRef = useRef<{ appendAdversaryUser: (user: ExternalUser) => void }>(null)
  const contactListRef = useRef<{ appendContactUser: (user: ExternalUser) => void }>(null)
  const { id: currentUserId, firstName, lastName, roles } = currentUser || {}
  const { role = null } = roles && roles[0] ? roles[0] : {}
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const fullName = `${firstName || ''} ${lastName || ''}`
  const label = fullName || ''
  const lasStepIndex = CREATE_CASE_STEPS.length - 1
  const isConsultMatter = pathname.includes('consult')
  const methods = useForm<CreateCaseCustomInput>({
    mode: 'all',
    resolver: yupResolver(createCaseValidationSchema, {
      context: {
        isConsultMatter
      }
    }),

    defaultValues: {
      title: '',
      docketNo: '',
      description: '',
      lawType: { id: '', label: '' },
      caseType: { id: '', label: '' },
      assignees: [{ id: currentUserId, label: label, role: role! }],
      court: { id: '', label: '' },
      client: { id: '', email: '', firstName: '', lastName: '', clientNumber: '', billDeliveryMethods: [] },
      clientUsers: [{ id: '', email: '', firstName: '', lastName: '', clientNumber: '', billDeliveryMethods: [] }],
      adversaryUsers: [{ id: '', email: '', firstName: '', lastName: '' }],
      judgeUser: { id: '', email: '', firstName: '', lastName: '' },
      advantageCode: '',
      caseNumber: '',
      status: undefined,
      judgeUserId: '',
      judgeUserRoleId: judgeRoleId,
      openDate: dayjs().format('YYYY-MM-DDTHH:mm:ssZ'),
      judge: {
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        phoneNumberExtension: '0'
      },
      references: [],
      isConsultMatter,
      isInternalMatter: false,
      openedBy: currentUser?.id,
      ...duplicateSource?.values,
      billing: {
        ...(isConsultMatter && { billingType: BillingType.Consult }),
        contingencyShare: null,
        billGenerationType: BillGenerationType.Amount,
        discountType: DiscountType.Percentage,
        billApproverId: null,
        interestRates: INTEREST_CONFIGURATION,
        ...withoutUndefined(duplicateSource?.values.billing || {})
      }
    }
  })

  const { reset, setValue } = methods
  const [createCaseMutation, { loading }] = useCreateCaseMutation({
    onError(error) {
      console.log('error in creating case -->', error)

      // The nested `extensions.exception.response.response` shape only exists for some server
      // errors. When it was missing NOTHING was shown at all — the user saw the spinner stop and
      // assumed the click had not registered, so they pressed Create Matter again, and each retry
      // left another half-built matter behind. This always resolves to something showable.
      //
      // Server text is only trusted for 4xx (the validation messages this screen raises). A 5xx
      // carries the raw driver error — e.g. 'insert or update on table "CaseReferences" violates
      // foreign key constraint …' — which means nothing to the user and leaks schema internals.
      const resolveMessage = (): string => {
        for (const { extensions } of error.graphQLErrors || []) {
          const exception = extensions?.exception as (ErrorException & { status?: number }) | undefined
          if (!exception || (exception.status ?? 500) >= 500) continue

          const { response } = exception
          const resolved =
            response?.response?.error || response?.response?.message || response?.error || response?.message

          if (resolved) return resolved
        }

        return 'Matter could not be created. Please try again, and contact support if the problem continues.'
      }

      const message = resolveMessage()
      Alert.error(message === 'Case number already exists' ? 'Matter number already exists' : message)
    },

    onCompleted(data) {
      if (data) {
        const { createCase } = data

        if (createCase) {
          const { response, case: matter } = createCase
          const { status } = response || {}

          if (status === 200) {
            Alert.success(CASE_CREATED_SUCCESS)
            reset()
            navigate(`/matters/matter-detail/${matter?.id}`)
          }
        }
      }
    }
  })

  const [getCLientByClientNo] = useGetClientByClientNumberLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: {
      clientNumber: clientNo || ''
    },
    onCompleted: data => {
      const { getClientByClientNumber } = data || {}
      const { externalUser } = getClientByClientNumber || {}
      if (externalUser) {
        setValue('client', {
          id: externalUser?.id ?? '',
          email: externalUser?.email ?? '',
          firstName: externalUser?.firstName ?? '',
          lastName: externalUser?.lastName ?? '',
          clientNumber: externalUser?.clientNumber ?? '',
          workPhone: externalUser?.phoneNumber ?? '',
          billDeliveryMethods: [],
          defaultBillingAddress: formatAddress(externalUser?.addresses)
        })
      }
    }
  })

  const handleNext = async () => {
    const valid = await methods.trigger(stepFields[activeStep].fields)
    if (valid) {
      setActiveStep(prevActiveStep => prevActiveStep + 1)
    }
  }

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1)
  }

  const handleStepClick = (index: number) => {
    setActiveStep(index)
  }
  const handleCreateCase = async (data: CreateCaseCustomInput) => {
    const {
      title,
      description,
      caseType,
      advantageCode,
      caseNumber,
      consultMatterNumber,
      adversaryUsers,
      clientUsers,
      client,
      adversary,
      court,
      assignees,
      docketNo,
      judgeUser,
      judge,
      legalTeam,
      openDate,
      closeDate,
      completedDate,
      billing,
      setting,
      references,
      isConsultMatter,
      isInternalMatter,
      profitCenter,
      location,
      openedBy,
      ccEmailBillsTo,
      consultAttorneyId,
    } = data
    const { firstName, lastName, email, phoneNumber, phoneNumberExtension } = judge || {}
    const validatePhoneNumber = phone(phoneNumber!, { country: 'USA' })
    const { phoneNumber: validPhoneNumber } = validatePhoneNumber
    const assigneeIds = assignees?.map(item => item.id)
    const splitBillEnabled = billing.billingType === BillingType.Hourly && billing.splitBillEnabled

    const legalTeamData = legalTeam
      ?.filter(item => !!item.userId)
      ?.map(item => ({
        userType: item.userType,
        userId: item.userId,
        hourlyRate: Number(item.hourlyRate)
      }))

    const clientUsersData = clientUsers
      .concat({ ...client, caseRole: ExternalUserCaseRole.PrimaryClient })
      ?.filter(item => !!item?.id && !!item?.caseRole)
      ?.map(item => ({
        userId: item.id,
        externalUserCaseRole: item.caseRole!,
        userRoleId: item.caseRole === ExternalUserCaseRole.PrimaryClient ? clientRoleId : item.userRoleId || '',
        splitBillShare: splitBillEnabled && item.isBillParticipant ? item.splitBillShare : null,
        billDeliveryMethods: item.billDeliveryMethods,
        billingAddress: item.billingAddress,
        billingPhoneNumber: item.billingPhoneNumber,
        billingEmail: item.billingEmail
      }))
    const adversaryUsersData = adversaryUsers
      .concat({ ...adversary, caseRole: ExternalUserCaseRole.PrimaryAdversary })
      ?.filter(item => !!item?.id && !!item?.caseRole)
      ?.map(item => ({
        userId: item.id,
        externalUserCaseRole: item.caseRole!,
        userRoleId: adversaryRoleId
      }))

    if (splitBillEnabled) {
      const totalSplitBillShare = clientUsersData.reduce((acc, curr) => acc + (curr.splitBillShare || 0), 0)
      if (totalSplitBillShare !== 100) {
        Alert.error(`Split bill share must total 100%. Current total: ${totalSplitBillShare.toFixed(2)}%`)
        return
      }
    }

    await createCaseMutation({
      variables: {
        createCaseInput: {
          title: title?.trim(),
          docketNo,
          description: description?.trim(),
          caseTypeId: caseType.id,
          advantageCode,
          caseNumber: !!caseNumber ? caseNumber : undefined,
          consultMatterNumber: !!consultMatterNumber ? consultMatterNumber : undefined,
          courtId: court?.id,
          status: CaseStatus?.Active,
          assigneeIds,
          judgeUserId: judgeUser?.id,
          judgeUserRoleId: judgeRoleId,
          billing: {
            ...billing,
            paymentPlanStartDate: billing.paymentPlanStartDate || null,
            pauseBillingUntil: billing.pauseBillingUntil ? endOfDay(new Date(billing.pauseBillingUntil)) : null,
            splitBillEnabled
          },
          isConsultMatter,
          isInternalMatter,
          setting: {
            sendFlatFeeHoursExhaustedNotification: setting?.sendFlatFeeHoursExhaustedNotification,
            flatFeeHoursNotificationThreshold: setting?.notifyOnFlatFeeHoursThreshold
              ? Number(setting.flatFeeHoursNotificationThreshold)
              : null
          },
          profitCenterId: profitCenter.id,
          locationId: location.id,
          judge: {
            firstName: firstName?.trim() || '',
            lastName: lastName?.trim() || '',
            email: email || '',
            phoneNumber: validPhoneNumber || '',
            status: ExternalUserStatus.Active,
            externalUserRoleId: judgeRoleId || '',
            phoneNumberExtension
          },
          references: references.filter(reference => !!reference.referenceId),
          legalTeam: legalTeamData,
          openDate: openDate ? new Date(openDate).toISOString() : null,
          closeDate: closeDate ? new Date(closeDate).toISOString() : null,
          completedDate: completedDate ? new Date(completedDate).toISOString() : null,
          caseParties: [...clientUsersData, ...adversaryUsersData],
          openedBy: openedBy ?? undefined,
          ccEmailBillsTo: ccEmailBillsTo || undefined,
          // Consults only. The field is not rendered for regular matters, so it is undefined there and the
          // API pins it to null for non-consults regardless.
          consultAttorneyId: isConsultMatter ? consultAttorneyId || undefined : undefined
        }
      }
    })
  }
  const onSubmit: SubmitHandler<CreateCaseCustomInput> = async (data: CreateCaseCustomInput) => {
    const { legalTeam } = data
    const isResponsibleAttorney1 = legalTeam?.find(item => item.userType === UserType.ResponsibleAttorney_1)
    const isOriginatorAttorney = legalTeam?.find(item => item.userType === UserType.OriginatorAttorney)
    const validUsers = isConsultMatter
      ? !!isResponsibleAttorney1?.userId && !!isOriginatorAttorney?.userId
      : !!isOriginatorAttorney?.userId

    validUsers ? await handleCreateCase(data) : Alert.error('Please select Originating and Responsible 1 Attorney')
  }

  useEffect(() => {
    if (newClientAdded) {
      clientListRef.current?.appendClientUser(newClientAdded)
    }
  }, [newClientAdded])

  useEffect(() => {
    if (newAdversaryAdded) {
      adversaryListRef.current?.appendAdversaryUser(newAdversaryAdded)
    }
  }, [newAdversaryAdded])

  useEffect(() => {
    if (clientNo)
      getCLientByClientNo({
        variables: {
          clientNumber: clientNo
        }
      })
  }, [clientNo, getCLientByClientNo])

  const handleInvalidMatter = () => {
    const errorStep = invalidMatterClick(methods.formState.errors)

    if (errorStep !== undefined) {
      setActiveStep(errorStep)
      const stepName = CREATE_CASE_STEPS[errorStep]?.heading || `Step ${errorStep + 1}`
      Alert.error(`Please complete all required fields in "${stepName}".`)
    } else {
      Alert.error('Please complete all required fields.')
    }
  }

  return (
    <Grid container px={3} py={4}>
      <Grid item xs={12} md={3}>
        <CaseStepper activeStep={activeStep} onStepClick={handleStepClick} />
      </Grid>

      <FormProvider {...methods}>
        <Grid item xs={12} md={8} lg={7} component="form" autoComplete="off" onSubmit={methods.handleSubmit(onSubmit)}>
          {duplicateSource && (
            <MuiAlert severity="info" sx={{ mb: 2, mt: { xs: 3, md: 0 } }}>
              Duplicating {duplicateSource.label}. Review the details below, then create the new{' '}
              {isConsultMatter ? 'consult' : 'matter'}.
            </MuiAlert>
          )}
          <Typography
            variant="h3"
            textTransform={'uppercase'}
            fontSize={16}
            fontWeight={700}
            mb={3}
            mt={{ xs: 3, md: 0 }}
          >
            {`${isConsultMatter && activeStep === 0 ? 'Consult Details' : CREATE_CASE_STEPS[activeStep]?.heading}`}
          </Typography>

          {activeStep === 0 && <BasicDetails selectedLawTypeId={duplicateSource?.selectedLawTypeId} />}
          {activeStep === 1 && (
            <CaseParties
              isConsultMatter={isConsultMatter}
              clientListRef={clientListRef}
              adversaryListRef={adversaryListRef}
              contactListRef={contactListRef}
            />
          )}
          {activeStep === 2 && <LegalTeam />}
          {activeStep === 3 && <BillingDetails />}
          {activeStep === 4 && <References />}

          <Box display="flex" justifyContent="flex-end" gap={3} mt={5}>
            {activeStep > 0 && (
              <Button variant="contained" color="inherit" sx={{ px: 6, backgroundColor: 'white' }} onClick={handleBack}>
                Back
              </Button>
            )}
            {activeStep === lasStepIndex && (
              <Button
                variant="contained"
                color="primary"
                disabled={loading || submitting}
                endIcon={(loading || submitting) && <CircularProgress size={20} color="inherit" />}
                sx={{ px: 8 }}
                onClick={async e => {
                  e.preventDefault()

                  if (submittingRef.current) return
                  submittingRef.current = true
                  setSubmitting(true)

                  try {
                    const valid = await methods.trigger()
                    if (valid) await methods.handleSubmit(onSubmit)()
                    else handleInvalidMatter()
                  } finally {
                    submittingRef.current = false
                    setSubmitting(false)
                  }
                }}
              >
                Create Matter
              </Button>
            )}
            {activeStep < lasStepIndex && (
              <Button variant="contained" color="primary" sx={{ px: 10 }} onClick={handleNext}>
                Next
              </Button>
            )}
          </Box>
        </Grid>
      </FormProvider>

      <AddNewClient setNewExternalUser={setNewClientAdded} />
      <AddNewAdversary setNewExternalUser={setNewAdversaryAdded} />
    </Grid>
  )
}

const DuplicateCase: FC<{ sourceCaseId: string }> = ({ sourceCaseId }) => {
  const { currentUser } = useContext(AuthContext)
  const { judgeRoleId, clientRoleId, contactRoleId, allLawTypes } = useContext(CaseContext)

  const { data, loading, error } = useFetchCaseQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: { caseInput: { id: sourceCaseId } },
    onError() {
      Alert.error('Unable to load the matter to duplicate')
    }
  })

  const sourceCase = data?.fetchCase?.case as CasePayload['case']
  // The mapper resolves law types and party roles from the case context, so wait for those as well.
  const contextReady = !!allLawTypes && !!clientRoleId

  const duplicateSource = useMemo<DuplicateSource | undefined>(() => {
    if (!sourceCase || !contextReady) return undefined

    const context = { judgeRoleId, clientRoleId, contactRoleId, allLawTypes, currentUserId: currentUser?.id }
    const { caseNumber, consultMatterNumber, isConsultMatter, title, caseType } = sourceCase
    const matterNo = isConsultMatter ? `Consult Matter #${consultMatterNumber}` : `Matter #${caseNumber}`

    return {
      values: buildDuplicateCaseValues(sourceCase, context),
      selectedLawTypeId: caseType?.lawTypeId,
      label: title ? `${matterNo} - ${title}` : matterNo
    }
  }, [sourceCase, contextReady, judgeRoleId, clientRoleId, contactRoleId, allLawTypes, currentUser?.id])

  // Nothing to copy from: fall back to a blank form rather than leaving the page empty.
  if (error) return <CreateCaseForm />

  if (loading || !duplicateSource)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    )

  // useForm only reads its defaults on mount, so the form is mounted once the source matter is in.
  return <CreateCaseForm key={sourceCaseId} duplicateSource={duplicateSource} />
}

const CreateNewCase: FC = () => {
  // Only the duplicate routes (/matters/duplicate/:id, /consults/duplicate/:id) carry an id.
  const { id: duplicateFrom } = useParams()

  return duplicateFrom ? <DuplicateCase sourceCaseId={duplicateFrom} /> : <CreateCaseForm />
}

export default CreateNewCase
